/**
 * Registre des champs paramétrables connus par type d'étape v2.
 *
 * Utilisé dans l'interface de configuration des paramètres de traitement
 * pour proposer une sélection de champs sans avoir à saisir un jsonpath manuellement.
 *
 * Structure de chaque entrée :
 *   { label: string, jsonpath: string }
 *
 * Cas particulier "projet" : pas de champs statiques ici.
 * Les champs disponibles sont chargés dynamiquement depuis child_project.details.treatment_params.
 *
 * Cas particulier "fonction" (script personnalisé) : pas de champs à cibler.
 * Les paramètres sont accessibles directement via la variable `treatment_params` dans le script.
 */

export const STEP_KNOWN_FIELDS = {

    // ── IA ──────────────────────────────────────────────────────────────────
    "chatgpt_v2": [
        { label: "Modèle",              jsonpath: "$.model" },
        { label: "Température",         jsonpath: "$.temperature",    type: "nombre" },
        { label: "Top P",               jsonpath: "$.top_p",          type: "nombre" },
        { label: "Message système",     jsonpath: "$.system" },
        { label: "Requête / prompt",    jsonpath: "$.content" },
        { label: "Colonne résultat",    jsonpath: "$.column_result" },
    ],

    // ── Lignes & Colonnes ────────────────────────────────────────────────────
    "split_json": [
        { label: "Colonne à extraire",          jsonpath: "$.column" },
        { label: "Nombre de passes",            jsonpath: "$.time",          type: "nombre" },
        { label: "Premier élément seulement",   jsonpath: "$.only_first",    type: "booleen" },
        { label: "Supprimer la colonne source", jsonpath: "$.delete_column", type: "booleen" },
        { label: "Conserver les lignes vides",  jsonpath: "$.keep_empty",    type: "booleen" },
    ],

    "split": [
        { label: "Colonne à découper",  jsonpath: "$.column" },
        { label: "Séparateur",          jsonpath: "$.separator" },
        { label: "Intervalle",          jsonpath: "$.interval",      type: "nombre" },
    ],

    "sort": [
        // Tous les champs sont dans le tableau $.columns — pas de scalaires racine
    ],

    "fusionner lignes": [
        { label: "Colonne clé",             jsonpath: "$.column_id" },
        { label: "Séparateur",              jsonpath: "$.separator" },
        { label: "Garder les fichiers",     jsonpath: "$.keep_files", type: "booleen" },
    ],

    "chercher et classer": [
        // Les recherches sont dans le tableau $.find — pas de scalaires racine
    ],

    "supprimer lignes": [
        { label: "Garder les lignes correspondantes", jsonpath: "$.keep", type: "booleen" },
    ],

    "mapping colonnes": [
        // Pas de champs scalaires simples — structure de mapping complexe
    ],

    "correspondance_v2": [
        // key_columns et values_column sont des tableaux — pas de remplacement simple
    ],

    // ── Import / Export ──────────────────────────────────────────────────────
    "mail": [
        { label: "Serveur SMTP",        jsonpath: "$.host" },
        { label: "Login",               jsonpath: "$.login" },
        { label: "Adresse expéditeur",  jsonpath: "$.from" },
        { label: "Destinataire",        jsonpath: "$.to" },
        { label: "Colonne objet",       jsonpath: "$.subject" },
        { label: "Colonne corps",       jsonpath: "$.content" },
    ],

    "xlsx": [
        { label: "Extension",       jsonpath: "$.extension" },
        { label: "Nom de l'onglet", jsonpath: "$.name_sheet" },
        { label: "Images",          jsonpath: "$.image",  type: "booleen" },
    ],

    "file": [
        { label: "Colonne fichier", jsonpath: "$.column" },
    ],

    // ── Services / Connecteurs ───────────────────────────────────────────────
    "api": [
        { label: "URL",         jsonpath: "$.url" },
        { label: "Méthode",     jsonpath: "$.method" },
        { label: "Colonne URL", jsonpath: "$.column_url" },
    ],

    "firecrawl": [
        { label: "Colonne URL",         jsonpath: "$.column" },
        { label: "Colonne résultat",    jsonpath: "$.column_result" },
        { label: "Limite",              jsonpath: "$.limit",          type: "nombre" },
        { label: "Pays",                jsonpath: "$.country" },
        { label: "Langue",              jsonpath: "$.lang" },
    ],

    "translate": [
        { label: "Langue source",   jsonpath: "$.source" },
        { label: "Langue cible",    jsonpath: "$.target" },
        // $.columns est un tableau — pas de colonne scalaire racine
    ],

    "send_notifications": [
        { label: "Titre",                   jsonpath: "$.column_title" },
        { label: "Message",                 jsonpath: "$.column_message" },
        { label: "Colonne résultat",        jsonpath: "$.column_result" },
        { label: "Colonne erreur",          jsonpath: "$.column_error" },
        { label: "Colonne identifiant",     jsonpath: "$.column_identifiant" },
        { label: "Unique (notif propre)",   jsonpath: "$.unique",   type: "booleen" },
    ],

    "agenda": [
        { label: "Intervalle de répétition",    jsonpath: "$.repeatInterval" },
        { label: "Date de déclenchement",       jsonpath: "$.nextRunAt",        type: "date" },
        { label: "Ne pas doublon",              jsonpath: "$.only_one",         type: "booleen" },
    ],

    "prestashop": [
        { label: "URL boutique",    jsonpath: "$.url" },
        { label: "Clé API",         jsonpath: "$.key" },
        { label: "Action",          jsonpath: "$.action" },
    ],

    // ── Base de données ──────────────────────────────────────────────────────
    "base de données": [
        { label: "Table",                   jsonpath: "$.table" },
        { label: "Personnaliser SELECT",    jsonpath: "$.select" },
        { label: "Fin de requête",          jsonpath: "$.end" },
    ],

    // ── Règles ───────────────────────────────────────────────────────────────
    "rules": [
        // Structure complexe — laisser jsonpath libre
    ],

    // ── Fonctions ────────────────────────────────────────────────────────────
    "fonctions excel": [
        // Champs définis dans les formules — pas de jsonpath racine simple
    ],

    // ── "projet" : champs chargés dynamiquement depuis child_project.details.treatment_params ──
    // "fonction" : paramètres accessibles via la variable `treatment_params` dans le script
};

/**
 * Retourne le type (type_key) suggéré pour un champ donné.
 * @param {string} action - Nom de l'action
 * @param {string} jsonpath - JSONPath du champ
 * @returns {string} type_key : "texte", "nombre", "booleen", etc.
 */
export function getFieldType(action, jsonpath) {
    var fields = STEP_KNOWN_FIELDS[action] || [];
    var field = fields.find(f => f.jsonpath === jsonpath);
    return (field && field.type) ? field.type : "texte";
}

/**
 * Retourne les champs connus pour une action donnée.
 * @param {string} action - Nom de l'action (ex: "split_json")
 * @returns {Array<{label: string, jsonpath: string}>}
 */
export function getKnownFields(action) {
    return STEP_KNOWN_FIELDS[action] || [];
}

/**
 * Indique si une action utilise des champs dynamiques (projet fils)
 * plutôt que le registre statique.
 * @param {string} action
 * @returns {boolean}
 */
export function hasDynamicFields(action) {
    return action === "projet";
}

/**
 * Indique si une action est un script personnalisé
 * (tous les paramètres accessibles via `treatment_params`, pas de target jsonpath).
 * @param {string} action
 * @returns {boolean}
 */
export function isScriptAction(action) {
    return action === "fonction";
}
